import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import NotFound from "pages/NotFound";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { SiteSettingsProvider } from "./context/SiteSettingsContext";

// Lazy load pages for better code splitting
// ComingSoon is not lazy loaded as it's the landing page
import ComingSoon from "./pages/ComingSoon";

const CaseStudiesShowcase = lazy(() => import("./pages/case-studies-showcase"));
const ServicesArchitectureHub = lazy(() => import("./pages/services-architecture-hub"));
const ContactSchedulingHub = lazy(() => import("./pages/contact-scheduling-hub"));
const AdaptiveHomepageHub = lazy(() => import("./pages/adaptive-homepage-hub"));
const DynamicPortfolioEcosystem = lazy(() => import("./pages/dynamic-portfolio-ecosystem"));
const AboutPage = lazy(() => import("./pages/about"));
const Shop = lazy(() => import("./pages/shop"));
const Dashboard = lazy(() => import("./pages/dashboard"));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="text-center space-y-4">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto"></div>
      <p className="text-muted-foreground">Loading...</p>
    </div>
  </div>
);

// Protected Route Component - requires authentication
const ProtectedRoute = ({ children }) => {
  const isAuthenticated = sessionStorage.getItem('site_authenticated') === 'true';

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Auth Required Route Component - requires Firebase authentication
const AuthRequiredRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const Routes = () => {
  return (
    <AuthProvider>
      <SiteSettingsProvider>
        <BrowserRouter>
        <ErrorBoundary>
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <RouterRoutes>
            {/* Coming Soon page as default */}
            <Route path="/" element={<ComingSoon />} />

            {/* Protected routes */}
            <Route
              path="/home"
              element={
                <ProtectedRoute>
                  <AdaptiveHomepageHub />
                </ProtectedRoute>
              }
            />
            <Route
              path="/case-studies-showcase"
              element={
                <ProtectedRoute>
                  <CaseStudiesShowcase />
                </ProtectedRoute>
              }
            />
            <Route
              path="/services-architecture-hub"
              element={
                <ProtectedRoute>
                  <ServicesArchitectureHub />
                </ProtectedRoute>
              }
            />
            <Route
              path="/contact-scheduling-hub"
              element={
                <ProtectedRoute>
                  <ContactSchedulingHub />
                </ProtectedRoute>
              }
            />
            <Route
              path="/adaptive-homepage-hub"
              element={
                <ProtectedRoute>
                  <AdaptiveHomepageHub />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dynamic-portfolio-ecosystem"
              element={
                <ProtectedRoute>
                  <DynamicPortfolioEcosystem />
                </ProtectedRoute>
              }
            />
            <Route
              path="/about"
              element={
                <ProtectedRoute>
                  <AboutPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/shop"
              element={
                <ProtectedRoute>
                  <Shop />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <AuthRequiredRoute>
                  <Dashboard />
                </AuthRequiredRoute>
              }
            />
            <Route path="*" element={<NotFound />} />
          </RouterRoutes>
        </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
      </SiteSettingsProvider>
    </AuthProvider>
  );
};

export default Routes;
